<template>
  <div class="container-fluid py-4">

    <!-- Breadcrumb -->
    <div class="row mb-4">
      <div class="col-12 d-flex align-items-center">
        <router-link to="/bonus-list" class="text-decoration-none me-2">
          <span class="text-muted">{{ $t('editbonuses.breadcrumb.list') }}</span>
        </router-link>
        <span class="text-muted">/</span>
        <h6 class="mb-0 fw-bold ms-2">{{ $t('editbonuses.title') }}</h6>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading && !form.title" class="row">
      <div class="col-12 text-center py-5">
        <i class="fas fa-spinner fa-spin fa-2x text-primary"></i>
        <p class="mt-2">{{ $t('editbonuses.loading') }}</p>
      </div>
    </div>

    <!-- Card -->
    <div v-else class="row">
      <div class="col-12">
        <div class="card mb-4 shadow-sm">

          <div class="card-header cap-header mx-3 mt-3 d-flex align-items-center justify-content-between">
            <h6 class="text-white text-capitalize m-0">{{ $t('editbonuses.title') }}</h6>

            <router-link to="/bonus-list" class="btn btn-outline-light btn-sm">
              <i class="fas fa-arrow-left me-1"></i> 
              {{ $t('editbonuses.back') }}
            </router-link>
          </div>

          <div class="card-body px-4">

            <form @submit.prevent="submitForm">
              <div class="row g-3">

                <!-- Title -->
                <div class="col-md-8">
                  <label class="form-label fw-semibold">{{ $t('editbonuses.fields.title') }} *</label>
                  <input
                    type="text"
                    class="form-control"
                    v-model.trim="form.title"
                    :class="{ 'is-invalid': errors.title }"
                    :placeholder="$t('editbonuses.placeholders.title')"
                    required
                  />
                  <div v-if="errors.title" class="invalid-feedback">
                    {{ errors.title[0] }}
                  </div>
                </div>

                <!-- Status -->
                <div class="col-md-4">
                  <label class="form-label fw-semibold">{{ $t('editbonuses.fields.status') }}</label>
                  <div class="form-check form-switch mt-2">
                    <input id="activeToggle" class="form-check-input" type="checkbox" v-model="form.active"
                           style="cursor:pointer;accent-color:#b03a45;">
                    <label class="form-check-label" for="activeToggle">
                      {{ form.active ? $t('editbonuses.status.active') : $t('editbonuses.status.inactive') }}
                    </label>
                  </div>
                </div>

                <!-- Date -->
                <div class="col-md-6">
                  <label class="form-label fw-semibold">{{ $t('editbonuses.fields.date') }} *</label>

                  <div class="d-flex gap-2">
                    <select class="form-select" v-model="selectedMonth" @change="updateDate"
                            :class="{ 'is-invalid': errors.date }">
                      <option value="">{{ $t('editbonuses.fields.month') }}</option>
                      <option v-for="(m,i) in months" :key="i" :value="i+1">{{ m }}</option>
                    </select>

                    <select class="form-select" v-model="selectedYear" @change="updateDate"
                            :class="{ 'is-invalid': errors.date }">
                      <option value="">{{ $t('editbonuses.fields.year') }}</option>
                      <option v-for="y in availableYears" :key="y" :value="y">{{ y }}</option>
                    </select>
                  </div>

                  <div v-if="errors.date" class="invalid-feedback d-block">
                    {{ errors.date[0] }}
                  </div>
                </div>

                <!-- Document -->
                <div class="col-md-6">
                  <label class="form-label fw-semibold">{{ $t('editbonuses.fields.document') }}</label>

                  <div class="mb-2">
                    <a v-if="form.document_url" :href="form.document_url" target="_blank"
                       class="btn btn-outline-dark btn-sm">
                      <i class="fas fa-download me-1"></i> 
                      {{ $t('editbonuses.document.download') }}
                    </a>
                    <span v-else class="text-muted small">— {{ $t('editbonuses.document.none') }} —</span>
                  </div>

                  <input ref="fileRef" type="file" class="form-control"
                         accept=".pdf,.doc,.docx,.xls,.xlsx" @change="onFileChange">

                  <div v-if="fileName" class="small mt-1">
                    {{ $t('editbonuses.selected') }} 
                    <strong>{{ fileName }}</strong>
                    <button type="button" class="btn btn-link btn-sm" @click="clearFile">
                      {{ $t('editbonuses.removeFile') }}
                    </button>
                  </div>
                </div>

                <!-- Original values -->
                <div class="col-12" v-if="originalData">
                  <div class="p-3 rounded border bg-light small">
                    <div>{{ $t('editbonuses.current.title') }} <strong>{{ originalData.title }}</strong></div>
                    <div>{{ $t('editbonuses.current.date') }} <strong>{{ originalData.date }}</strong></div>
                    <div>
                      {{ $t('editbonuses.current.status') }}
                      <span class="badge" :class="originalData.active ? 'bg-success' : 'bg-secondary'">
                        {{ originalData.active ? $t('editbonuses.status.active') : $t('editbonuses.status.inactive') }}
                      </span>
                    </div>
                  </div>
                </div>

              </div>

              <!-- Actions -->
              <div class="d-flex justify-content-end gap-2 mt-3">
                <router-link to="/bonus-list" class="btn btn-outline-secondary">
                  <i class="fas fa-times me-1"></i> {{ $t('editbonuses.cancel') }}
                </router-link>

                <button type="submit" class="btn btn-cap"
                        :disabled="saving || !isFormValid || !hasChanges">
                  <i v-if="saving" class="fas fa-spinner fa-spin me-1"></i>
                  <i v-else class="fas fa-save me-1"></i>
                  {{ saving ? $t('editbonuses.saving') : $t('editbonuses.update') }}
                </button>
              </div>

              <div v-if="!hasChanges && form.title" class="mt-2 small text-secondary">
                <i class="fas fa-info-circle me-1"></i>
                {{ $t('editbonuses.noChanges') }}
              </div>

              <div v-if="lastError" class="alert alert-danger mt-3">{{ lastError }}</div>

            </form>

          </div>

        </div>
      </div>
    </div>

  </div>
</template>

<script>
import axios from '@/api'

export default {
  name: 'EditBonus',
  data() {
    return {
      bonusId: null,
      loading: true,
      saving: false,
      lastError: '',
      errors: {},
      form: {
        title: '',
        date: '',           // "MM-YYYY"
        active: true,
        document_url: null, // pour affichage
      },
      selectedMonth: '',
      selectedYear: '',
      fileRef: null,
      file: null,
      fileName: '',
      originalData: null,
      months: [
        'January','February','March','April','May','June',
        'July','August','September','October','November','December'
      ]
    }
  },
  computed: {
    availableYears() {
      const y = new Date().getFullYear()
      const years = []
      for (let k = y - 5; k <= y + 5; k++) years.push(k)
      return years
    },
    isFormValid() {
      return this.form.title.trim() !== '' && this.form.date && this.selectedMonth && this.selectedYear
    },
    hasChanges() {
      if (!this.originalData) return false
      return (
        this.form.title   !== this.originalData.title   ||
        this.form.date    !== this.originalData.date    ||
        this.form.active  !== this.originalData.active  ||
        !!this.file
      )
    }
  },
  mounted() {
    this.bonusId = this.$route.params.id
    if (!this.bonusId) {
      this.toast('error', 'Missing bonus ID')
      this.$router.push('/bonus-list')
      return
    }
    this.fetchFromIndex()
  },
  methods: {
    toast(kind, msg) {
      if (this.$toast && typeof this.$toast[kind] === 'function') this.$toast[kind](msg)
      else console[kind === 'error' ? 'error' : 'log'](msg)
    },

    // Pas de GET /{id} => on parcourt l’index paginé pour trouver l’item
    async fetchFromIndex(page = 1) {
      try {
        const { data } = await axios.get('/financial-requests/monthly-bonuses', { params: { page } })
        const rows = Array.isArray(data?.data) ? data.data : []
        const found = rows.find(b => String(b.id) === String(this.bonusId))
        if (found) {
          this.apply(found)
          return
        }
        // continuer à paginer si besoin
        if ((data?.current_page || 1) < (data?.last_page || 1)) {
          await this.fetchFromIndex((data.current_page || 1) + 1)
        } else {
          this.toast('error', 'Bonus not found')
          this.$router.push('/bonus-list')
        }
      } catch (e) {
        console.error('Error loading bonus:', e)
        this.toast('error', 'Failed to load bonus')
        this.$router.push('/bonus-list')
      } finally {
        this.loading = false
      }
    },

    apply(item) {
      this.originalData = {
        title: item.title || '',
        date: item.date || '',
        active: !!item.active,
        document_url: item.document_url || null,
      }
      this.form.title = this.originalData.title
      this.form.date = this.originalData.date
      this.form.active = this.originalData.active
      this.form.document_url = this.originalData.document_url

      if (this.form.date) {
        const [mm, yyyy] = this.form.date.split('-')
        this.selectedMonth = parseInt(mm)
        this.selectedYear = parseInt(yyyy)
      }
    },

    updateDate() {
      if (this.selectedMonth && this.selectedYear) {
        const mm = String(this.selectedMonth).padStart(2, '0')
        this.form.date = `${mm}-${this.selectedYear}`
      } else {
        this.form.date = ''
      }
    },

    onFileChange(e) {
      const f = e.target.files?.[0]
      if (!f) { this.clearFile(); return }
      const allowed = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      ]
      const tooBig = f.size > 10 * 1024 * 1024
      if (tooBig || (f.type && !allowed.includes(f.type))) {
        this.toast('error', 'Invalid file. Use pdf/doc/docx/xls/xlsx up to 10MB.')
        this.clearFile()
        return
      }
      this.file = f
      this.fileName = f.name
    },

    clearFile() {
      this.file = null
      this.fileName = ''
      if (this.$refs.fileRef) this.$refs.fileRef.value = ''
    },

    async submitForm() {
      if (!this.hasChanges) {
        this.toast('info', 'Nothing to save')
        return
      }
      this.saving = true
      this.errors = {}
      this.lastError = ''

      // multipart + override PUT
      const fd = new FormData()
      fd.append('_method', 'PUT')
      fd.append('title', this.form.title)
      fd.append('date', this.form.date)                 // MM-YYYY
      fd.append('active', this.form.active ? '1' : '0') // ✅ back attend in:true,false,1,0
      if (this.file) fd.append('document_path', this.file)

      try {
        await axios.post(`/financial-requests/monthly-bonuses/${this.bonusId}`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        this.toast('success', 'Bonus updated successfully')
        this.$router.push('/bonus-list')
      } catch (e) {
        console.error('update error', e)
        const status = e?.response?.status
        if (status === 422) {
          this.errors = e?.response?.data?.errors || {}
          this.lastError = 'Please fix the highlighted fields.'
          this.toast('error', 'Please fix the form errors')
        } else if (status === 404) {
          this.toast('error', 'Bonus not found')
          this.$router.push('/bonus-list')
        } else {
          this.lastError = e?.response?.data?.message || 'Failed to update bonus'
          this.toast('error', this.lastError)
        }
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.cap-header{
  background: linear-gradient(195deg,#b03a45,#8d2e38);
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0,0,0,.1);
  padding: .75rem 1rem;
}
.btn-cap{
  background-color:#b03a45;
  border-color:#b03a45;
  color:#fff;
}
.btn-cap:hover{
  background-color:#8d2e38;
  border-color:#8d2e38;
  color:#fff;
}
.card{ border:none; border-radius:12px; }
.shadow-sm{ box-shadow:0 .125rem .25rem rgba(0,0,0,.075)!important; }
</style>
